import http from '@/utils/http';
import { IAM_ENDPOINTS } from '@/config/api';

/**
 * Audit 令牌管理
 *
 * Audit 项目改造后，调用 Audit 接口需要持有 Audit 签发的令牌，
 * 而非 IAM 签发的令牌。这里通过 /api/v1/auth/exchange 接口
 * 凭 IAM 令牌换取 Audit 令牌，并在本地缓存，过期后自动重新换取。
 */

const AUDIT_TOKEN_KEY = 'auditAccessToken';
const AUDIT_TOKEN_EXPIRES_KEY = 'auditTokenExpiresAt';

// 提前量（毫秒）：距离过期还剩这么多时间就视为已过期，避免边界问题
const EXPIRY_BUFFER_MS = 60 * 1000;

// 当前进行中的换取请求，用于并发去重
let exchangePromise = null;

/**
 * 读取本地缓存的 Audit 令牌（未过期才返回）
 * @returns {string|null}
 */
function getCachedAuditToken() {
  const token = localStorage.getItem(AUDIT_TOKEN_KEY);
  const expiresAt = Number(localStorage.getItem(AUDIT_TOKEN_EXPIRES_KEY));
  if (!token || !expiresAt) return null;
  if (Date.now() >= expiresAt - EXPIRY_BUFFER_MS) return null;
  return token;
}

/**
 * 缓存 Audit 令牌
 * @param {string} accessToken
 * @param {number} expiresIn - 有效期（秒）
 */
function cacheAuditToken(accessToken, expiresIn) {
  localStorage.setItem(AUDIT_TOKEN_KEY, accessToken);
  const expiresAt = Date.now() + expiresIn * 1000;
  localStorage.setItem(AUDIT_TOKEN_EXPIRES_KEY, String(expiresAt));
}

/**
 * 清除本地缓存的 Audit 令牌
 */
export function clearAuditToken() {
  localStorage.removeItem(AUDIT_TOKEN_KEY);
  localStorage.removeItem(AUDIT_TOKEN_EXPIRES_KEY);
}

/**
 * 调用换取接口，用 IAM 令牌换取 Audit 令牌
 * 走现有 http 实例，请求拦截器会自动附带 IAM 令牌
 * @returns {Promise<string>} Audit accessToken
 */
async function exchangeToken() {
  const data = await http.post(IAM_ENDPOINTS.AUTH_EXCHANGE);
  const { accessToken, expiresIn } = data || {};
  if (!accessToken) {
    throw new Error('Audit token exchange failed: missing accessToken');
  }
  cacheAuditToken(accessToken, expiresIn || 3600);
  return accessToken;
}

/**
 * 获取有效的 Audit 令牌
 * - 优先返回缓存的未过期令牌
 * - 无缓存或已过期则自动换取
 * - 并发调用只会触发一次换取请求
 * @returns {Promise<string>}
 */
export async function getAuditToken() {
  const cached = getCachedAuditToken();
  if (cached) return cached;

  if (!exchangePromise) {
    exchangePromise = exchangeToken().finally(() => {
      exchangePromise = null;
    });
  }
  return exchangePromise;
}
